import React, { useEffect, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Box,
  Button,
  Container,
  Heading,
  HStack,
  Input,
  Progress,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";

function getSupportedMimeType() {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/ogg;codecs=opus",
    "audio/mp4",
  ];

  for (const mimeType of candidates) {
    if (window.MediaRecorder && MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return "audio/webm";
}

export default function LiveAudioJoinPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const initialRunId = searchParams.get("runId") || "";

  const [runId, setRunId] = useState(initialRunId);
  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [stats, setStats] = useState({ uploaded: 0, failed: 0, inFlight: 0 });

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const seqRef = useRef(0);
  const lastChunkStartedAtRef = useRef(null);

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    recorderRef.current = null;
    setIsRecording(false);
  };

  useEffect(() => {
    return () => {
      stopRecording();
    };
  }, []);

  const uploadChunk = async (blob, startedAt, endedAt) => {
    const seq = seqRef.current;
    seqRef.current += 1;

    setStats((prev) => ({ ...prev, inFlight: prev.inFlight + 1 }));

    try {
      const form = new FormData();
      form.append("token", token || "");
      form.append("runId", runId);
      form.append("seq", String(seq));
      form.append("startedAt", startedAt);
      form.append("endedAt", endedAt);
      form.append("audio", blob, `chunk-${seq}.webm`);

      const response = await fetch("/live-audio/upload-chunk", {
        method: "POST",
        body: form,
      });

      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.error || "Upload failed");
      }

      setStats((prev) => ({ ...prev, uploaded: prev.uploaded + 1 }));
    } catch (error) {
      setStats((prev) => ({ ...prev, failed: prev.failed + 1 }));
      toast({
        title: "Chunk Upload Failed",
        description: error.message || "Could not upload audio chunk",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setStats((prev) => ({ ...prev, inFlight: Math.max(0, prev.inFlight - 1) }));
    }
  };

  const startRecording = async () => {
    if (!token) {
      toast({
        title: "Invalid Link",
        description: "Missing live audio token",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!runId.trim()) {
      toast({
        title: "Run ID Required",
        description: "Please provide a valid run ID before recording",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsStarting(true);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const mimeType = getSupportedMimeType();
      const recorder = new MediaRecorder(stream, { mimeType });

      streamRef.current = stream;
      recorderRef.current = recorder;
      seqRef.current = 0;
      lastChunkStartedAtRef.current = new Date().toISOString();

      recorder.ondataavailable = async (event) => {
        if (!event.data || event.data.size <= 0 || !runId) return;

        const endedAt = new Date().toISOString();
        const startedAt = lastChunkStartedAtRef.current || endedAt;
        lastChunkStartedAtRef.current = endedAt;

        await uploadChunk(event.data, startedAt, endedAt);
      };

      recorder.onerror = (event) => {
        toast({
          title: "Recorder Error",
          description: event?.error?.message || "Audio recorder encountered an error",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      };

      recorder.start(5000);
      setIsRecording(true);
    } catch (error) {
      toast({
        title: "Microphone Access Failed",
        description: error.message || "Unable to start recording",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      stopRecording();
    } finally {
      setIsStarting(false);
    }
  };

  const totalChunks = stats.uploaded + stats.failed;
  const successPct = totalChunks > 0 ? Math.round((stats.uploaded / totalChunks) * 100) : 0;

  return (
    <Container maxW="container.md" py={10}>
      <VStack spacing={5} align="stretch">
        <Heading size="lg">Live Audio Participant Capture</Heading>
        <Text color="gray.400" fontSize="sm">
          Keep this page open while speaking. Audio uploads in small chunks every few seconds.
        </Text>

        <Alert status="info" borderRadius="md">
          <AlertIcon />
          <AlertDescription>
            This link is tokenized for live simulation capture. Do not share it outside your scenario team.
          </AlertDescription>
        </Alert>

        <Box bg="gray.800" p={4} borderRadius="md">
          <VStack align="stretch" spacing={3}>
            <Text fontSize="sm" color="gray.400">Run ID</Text>
            <Input
              value={runId}
              onChange={(e) => setRunId(e.target.value)}
              placeholder="run_..."
              bg="gray.700"
              isDisabled={isRecording}
            />
            <Text fontSize="xs" color="gray.500">Token: {token ? `${token.slice(0, 10)}...` : "missing"}</Text>
          </VStack>
        </Box>

        <HStack>
          {!isRecording ? (
            <Button colorScheme="green" onClick={startRecording} isLoading={isStarting}>
              Start Capture
            </Button>
          ) : (
            <Button colorScheme="red" onClick={stopRecording}>
              Stop Capture
            </Button>
          )}
          <Badge colorScheme={isRecording ? "green" : "gray"} px={3} py={1}>
            {isRecording ? "Recording" : "Idle"}
          </Badge>
        </HStack>

        <Box bg="gray.800" p={4} borderRadius="md">
          <VStack align="stretch" spacing={2}>
            <HStack justify="space-between">
              <Text fontSize="sm">Uploaded: {stats.uploaded}</Text>
              <Text fontSize="sm">Failed: {stats.failed}</Text>
              <Text fontSize="sm">In Flight: {stats.inFlight}</Text>
            </HStack>
            <Progress value={successPct} colorScheme="green" size="sm" borderRadius="md" />
            <Text fontSize="xs" color="gray.400">Upload success: {successPct}%</Text>
          </VStack>
        </Box>
      </VStack>
    </Container>
  );
}
